/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.14.0
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import { Collapse } from '@nexign/scc-ui-kit';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import type { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { LinkBlock } from '@components/common';
import { FormBlockStyled, FormControlStyled } from '@components/common/FormStyled/FormStyled';
import Textarea from '@components/common/Textarea/Textarea';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import { EAddressElement } from '@pages/PageAccountInfo/types/pageAccountInfoGeneralTypes';
import { makeSelectIsSubscriber } from '@pages/PageAuth/selectors/authSelectors';
import {
    getWizardImportPersonalDataUrl,
    isWizardImportPersonalDataExternalUrl,
} from '@pages/WizardImportPersonalData/utils/getWizardImportPersonalDataUrl';
import type { IRootReducer } from '@reducers/rootReducer';
import { openExternalRoute, openRoute } from '@routes/constants/externalRoutes';
import { htmlTranslationFormat } from '@utils/htmlTranslationFormat';

import withAsyncStateManagement from '../../asyncStateManagment/WithAsyncStateManagement';
import PageLocale from '../../internationalization/components/PageLocale';
import { TranslationFileNamesEnum } from '../../internationalization/constants';
import { makeSelectFormAddressTranslation, makeSelectTranslationCommon } from '../../internationalization/intSelectors';
import type { ICommonTranslation } from '../../internationalization/types';
import type { IFormAddressTranslation } from '../../internationalization/types/modules/formAddress';
import Modal from '../../modal/components/Modal';
import type { IModalRenderProps } from '../../modal/types/modalTypes';
import ButtonServiceUserDenied from '../../role/components/ButtonServiceUserDenied';
import { guardComponentType } from '../../role/reducer/guardReducer';
import { makeSelectGuardComponents } from '../../role/selectors/guardSelector';
import { wizardStorageClearStepsAction } from '../../wizard/wizardActions';
import { EWizardName } from '../../wizard/wizardConstants';
import type { IWizardStorageClearStepsPayload } from '../../wizard/wizardTypes';
import {
    FormAddressFieldsDependence,
    FormAddressFieldsSchema,
    FormAddressValidationSchema,
} from '../constant/formAddressScheme';
import { formAddressGetAsStringStartAction, formAddressSearchStartAction } from '../formAddressActions';
import formAddressReducer from '../formAddressReducer';
import type { IFormAddressReducer } from '../formAddressReducer';
import { formAddressSaga } from '../formAddressSaga';
import {
    makeSelectFormAddressConfiguration,
    makeSelectFormAddressIsInit,
    makeSelectFormAddressSearchFoundItems,
    makeSelectFormAddressSearchIsLoading,
    makeSelectFormAddressString,
} from '../formAddressSelectors';
import type {
    IAddressStringPayload,
    IFormAddressSearchStartPayload,
    TPartialFormAddressConfiguration,
} from '../types/formAddressActionTypes';
import { EFormAddressField, EFormAddressType } from '../types/formAddressTypes';
import type { IFormAddressConfiguration } from '../types/formAddressTypes';
import FormAddressCheckbox from './FormAddressCheckbox';
import FormAddressField from './FormAddressField';
import { FormAddressInfoBlockStyled, FormAddressStyled } from './styled/FormAddressStyled';
import { getAddressStringRequestValueAfterCheck, mapFormValuesToAddressRequest, trimSearchFields } from './utils';

export interface IFormAddressProps {
    initialize: () => void;
    onSubmit: (address: TPartialFormAddressConfiguration) => void;
    type: EFormAddressType;
}

const FormAddress = ({
    addressString,
    addressConfiguration,
    componentFeatures,
    isFormInit,
    isSubscriber,
    searchFoundItems,
    searchIsLoading,
    translationCommon,
    translationFormAddress,
    type,

    getAddressString,
    initialize,
    onCloseModal,
    onSubmit,
    search,
    wizardStorageClearStepsAction,
}: IConnectedProps & IDispatchProps & IModalRenderProps & IFormAddressProps) => {
    const [isPlanningElementEnabled, setPlanningElementEnabled] = useState(true);

    useEffect(() => {
        initialize();
    }, [initialize]);

    const isFieldDisabled = useCallback((type: EFormAddressField, { getFieldProps }): boolean => {
        // Проверка на заполненность полей выше (если они обязательны для заполнения)
        const fieldsForFill = FormAddressFieldsDependence[type]?.dependenceOnValue.filter(
            (field: EFormAddressField) => {
                // Значение поля выше по иерархии
                const fieldValue =
                    field === EFormAddressField.house
                        ? getFieldProps(field).value?.def
                        : getFieldProps(field).value?.id;
                // Обязательно ли данное для заполнения
                const fieldOptionalName = FormAddressFieldsDependence[field].disableOption;
                // Для некоторых полей верхне уровневое поле всегда обязательно для заполнения
                const requiredForChildren = FormAddressFieldsDependence[field].requiredForChildren;
                return !(
                    fieldValue ||
                    (fieldOptionalName && !requiredForChildren ? getFieldProps(fieldOptionalName).value : false)
                );
            },
        );
        const fieldOptionName = FormAddressFieldsDependence[type].disableOption;
        // Есть ли поля выше по иерархии обязательные для заполнения или
        return (
            fieldsForFill.length > 0 ||
            // или текущее поле необязательно для заполнения
            (fieldOptionName ? getFieldProps(fieldOptionName).value : false)
        );
    }, []);

    const clearFieldsAfterChange = useCallback((type: EFormAddressField, { setFieldValue, setFieldTouched }) => {
        FormAddressFieldsDependence[type].clearAfterChange?.forEach((field: EFormAddressField) => {
            setFieldValue(field, null, false);
            setFieldTouched(field, false, false);
        });
    }, []);

    const onFieldChange = useCallback(
        (type: EFormAddressField, address: TPartialFormAddressConfiguration, { setFieldValue, setFieldTouched }) => {
            switch (type) {
                case EFormAddressField.city:
                case EFormAddressField.planningElement:
                case EFormAddressField.street:
                    const value = address[type]?.def.trim();
                    if (value && value.length < 2) {
                        return;
                    }

                    if (type === EFormAddressField.city) {
                        setPlanningElementEnabled(true);
                    }
                    break;
            }

            const addressSearch = trimSearchFields(address);

            search({ type, address: addressSearch });
            clearFieldsAfterChange(type, { setFieldValue, setFieldTouched });
        },
        [search, clearFieldsAfterChange],
    );

    const onOptionalChecked = useCallback(
        (type: EFormAddressField, checked: boolean, { setFieldValue, setFieldTouched, values }) => {
            clearFieldsAfterChange(type, { setFieldValue, setFieldTouched });
            const addressStringRequestValue = getAddressStringRequestValueAfterCheck(type, checked, values);

            if (addressStringRequestValue) {
                getAddressString(addressStringRequestValue);
            }
        },
        [getAddressString, clearFieldsAfterChange],
    );

    // Если поля ЭПС и улица выбраны как необязательные, то дом блокировать нельзя
    const isFieldHouseRequired = useCallback(
        ({ getFieldProps }) =>
            getFieldProps(EFormAddressField.planningElementOptional).value &&
            getFieldProps(EFormAddressField.streetOptional).value,
        [],
    );

    /*
     * -------------------------------------------
     *           Event Handlers
     * -------------------------------------------
     */

    const onSelect = useCallback(
        (
            type: EFormAddressField,
            addressStringRequestValue: IAddressStringPayload,
            addressElementType?: EAddressElement | null,
        ) => {
            if (type === EFormAddressField.city && addressStringRequestValue.address.city) {
                setPlanningElementEnabled(addressElementType !== EAddressElement.PLANNING_ELEMENT);
            }
            getAddressString(addressStringRequestValue);
        },
        [getAddressString],
    );

    const onSubmitHandler = useCallback(
        (address: IFormAddressConfiguration) => {
            const addressInfo = mapFormValuesToAddressRequest(address);

            onSubmit({ ...addressInfo, addressString });
            onCloseModal();
        },
        [addressString, onCloseModal, onSubmit],
    );

    /*
     * -------------------------------------------
     *                Advice
     * -------------------------------------------
     */

    const adviceTranslation = useMemo(
        () => translationFormAddress.info.address[isSubscriber ? 'subs' : 'all'],
        [isSubscriber, translationFormAddress],
    );

    const onAdviceLinkClick = useCallback(() => {
        wizardStorageClearStepsAction({
            wizardName: EWizardName.IMPORT_PERSONAL_DATA,
        });
        const url = getWizardImportPersonalDataUrl(componentFeatures);
        isWizardImportPersonalDataExternalUrl(componentFeatures) ? openExternalRoute(url) : openRoute(url);
    }, [componentFeatures, wizardStorageClearStepsAction]);

    /*
     * -------------------------------------------
     *           Render
     * -------------------------------------------
     */

    const renderField = useCallback(
        (field: EFormAddressField) => (
            <FormAddressField
                {...FormAddressFieldsSchema[field]}
                disabled={isFieldDisabled}
                foundItems={searchFoundItems[field]}
                isLoading={searchIsLoading[field]}
                label={translationFormAddress.fields[field]}
                name={field}
                onChange={onFieldChange}
                onSelect={onSelect}
            />
        ),
        [isFieldDisabled, onFieldChange, onSelect, searchFoundItems, searchIsLoading, translationFormAddress],
    );

    const renderCheckbox = useCallback(
        (field: EFormAddressField, disabled?: boolean) => (
            <FormAddressCheckbox
                {...FormAddressFieldsSchema[field]}
                disabled={disabled}
                isDisabled={isFieldDisabled}
                label={translationFormAddress.fields[field]}
                name={field}
                onChange={onOptionalChecked}
            />
        ),
        [isFieldDisabled, onOptionalChecked, translationFormAddress],
    );

    return (
        <>
            <PageLocale lazy fileNames={[TranslationFileNamesEnum.formAddress]} />
            <Modal withoutPadding dataName="form-address-edit-modal" width="700px">
                <Modal.Title>{translationFormAddress.types[type].title}</Modal.Title>
                {type === EFormAddressType.subscriber && (
                    <FormAddressInfoBlockStyled>
                        {htmlTranslationFormat(adviceTranslation.text, {
                            link: <LinkBlock onClick={onAdviceLinkClick}>{adviceTranslation.link}</LinkBlock>,
                        })}
                    </FormAddressInfoBlockStyled>
                )}
                {isFormInit && (
                    <Formik
                        initialValues={addressConfiguration}
                        validationSchema={FormAddressValidationSchema}
                        onSubmit={onSubmitHandler}
                    >
                        {({
                            dirty,
                            getFieldProps,
                            isSubmitting,
                            isValid,
                            resetForm,
                            submitForm,
                        }: FormikProps<IFormAddressConfiguration>) => (
                            <FormAddressStyled>
                                <Modal.Body style={{ maxHeight: 400 }}>
                                    <FormBlockStyled>
                                        <Textarea
                                            dataName="form-address-string"
                                            disabled={true}
                                            rows={4}
                                            value={addressString}
                                        />
                                    </FormBlockStyled>
                                    {renderField(EFormAddressField.city)}
                                    <Collapse appear={false} isOpen={isPlanningElementEnabled}>
                                        <FormBlockStyled>
                                            {renderField(EFormAddressField.planningElement)}
                                            {renderCheckbox(EFormAddressField.planningElementOptional)}
                                        </FormBlockStyled>
                                    </Collapse>
                                    <FormBlockStyled>
                                        {renderField(EFormAddressField.street)}
                                        {renderCheckbox(EFormAddressField.streetOptional)}
                                    </FormBlockStyled>
                                    <div className="form-address-fields__group">
                                        <FormBlockStyled className="form-address-fields__house">
                                            {renderField(EFormAddressField.house)}
                                            <ArrowTooltip
                                                title={
                                                    isFieldHouseRequired({ getFieldProps })
                                                        ? translationFormAddress.fields.houseTooltip
                                                        : ''
                                                }
                                            >
                                                <span>
                                                    {renderCheckbox(
                                                        EFormAddressField.houseOptional,
                                                        isFieldHouseRequired({ getFieldProps }),
                                                    )}
                                                </span>
                                            </ArrowTooltip>
                                        </FormBlockStyled>
                                        {renderField(EFormAddressField.block)}
                                        <FormBlockStyled>
                                            {renderField(EFormAddressField.apartment)}
                                            {renderCheckbox(EFormAddressField.apartmentOptional)}
                                        </FormBlockStyled>
                                        {renderField(EFormAddressField.structure)}
                                    </div>
                                </Modal.Body>
                                <Modal.Control>
                                    <FormControlStyled>
                                        <ButtonServiceUserDenied
                                            disabled={
                                                !isValid ||
                                                isSubmitting ||
                                                !dirty ||
                                                !getFieldProps(EFormAddressField.city).value?.id
                                            }
                                            kind="medium"
                                            type="submit"
                                            onClick={submitForm}
                                        >
                                            {translationCommon.form.save}
                                        </ButtonServiceUserDenied>
                                        <LinkBlock
                                            onClick={() => {
                                                resetForm();
                                                onCloseModal();
                                            }}
                                        >
                                            {translationCommon.form.cancel}
                                        </LinkBlock>
                                    </FormControlStyled>
                                </Modal.Control>
                            </FormAddressStyled>
                        )}
                    </Formik>
                )}
            </Modal>
        </>
    );
};

type TReducerProps = Pick<
    IFormAddressReducer,
    'addressConfiguration' | 'isFormInit' | 'searchFoundItems' | 'searchIsLoading'
>;

interface IConnectedProps extends TReducerProps {
    addressString: string;
    componentFeatures: guardComponentType;
    isSubscriber: boolean;
    translationCommon: ICommonTranslation;
    translationFormAddress: IFormAddressTranslation;
}

export const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    addressConfiguration: makeSelectFormAddressConfiguration(),
    addressString: makeSelectFormAddressString(),
    componentFeatures: makeSelectGuardComponents(),
    isFormInit: makeSelectFormAddressIsInit(),
    isSubscriber: makeSelectIsSubscriber(),
    searchFoundItems: makeSelectFormAddressSearchFoundItems(),
    searchIsLoading: makeSelectFormAddressSearchIsLoading(),
    translationCommon: makeSelectTranslationCommon(),
    translationFormAddress: makeSelectFormAddressTranslation(),
});

interface IDispatchProps {
    getAddressString: (payload: IAddressStringPayload) => void;
    search: (payload: IFormAddressSearchStartPayload) => void;
    wizardStorageClearStepsAction: (payload: IWizardStorageClearStepsPayload) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    getAddressString: (payload) => dispatch(formAddressGetAsStringStartAction(payload)),
    search: (payload) => dispatch(formAddressSearchStartAction(payload)),
    wizardStorageClearStepsAction: (payload) => dispatch(wizardStorageClearStepsAction(payload)),
});

export default withAsyncStateManagement(
    connect(mapStateToProps, mapDispatchToProps)(FormAddress),
    // @ts-ignore
    { reducer: { formAddress: formAddressReducer }, sagas: formAddressSaga },
);
